import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  collection, query, where, getDocs, doc, updateDoc, addDoc, Timestamp, orderBy,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { bottleKeepStatusLabels } from '../types';

/**
 * ボトルキープ台帳。
 *
 * スナックの売上構成の柱でありながら、Firestore 側にも NightPro 側にも
 * この概念が無かった。入れた時の担当キャストを持たせることで、
 * ボトルバックの根拠にもなる。
 */

interface KeepRow {
  id: string;
  customerName: string;
  phoneNumber?: string;
  productName: string;
  quantity: number;
  openedAt: Date;
  openedByStaffName?: string;
  openedPrice: number;
  remainingPercent: number;
  expiresAt?: Date;
  status: 'kept' | 'consumed' | 'expired' | 'disposed' | 'transferred';
  location?: string;
}

const statusColor: Record<KeepRow['status'], 'success' | 'default' | 'warning' | 'error' | 'info'> = {
  kept: 'success',
  consumed: 'default',
  expired: 'warning',
  disposed: 'error',
  transferred: 'info',
};

const BottleKeeps: React.FC = () => {
  const { shop, user } = useAuth();
  const [rows, setRows] = useState<KeepRow[]>([]);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'kept' | 'expiring' | 'all'>('kept');
  const [search, setSearch] = useState('');

  const [editDialog, setEditDialog] = useState<{ open: boolean; row: KeepRow | null }>({ open: false, row: null });
  const [editRemaining, setEditRemaining] = useState(100);

  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState({
    customerName: '', phoneNumber: '', productName: '', quantity: 1,
    price: 0, staffId: '', location: '', keepMonths: 6,
  });

  const load = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setError(null);
    try {
      const [keepSnap, staffSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'bottleKeeps'),
          where('shopId', '==', shop.id),
          orderBy('openedAt', 'desc'),
        )),
        getDocs(query(
          collection(db, 'employees'),
          where('shopId', '==', shop.id),
          where('status', '==', 'active'),
        )),
      ]);
      setRows(keepSnap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          customerName: data.customerName ?? '',
          phoneNumber: data.phoneNumber ?? undefined,
          productName: data.productName ?? '',
          quantity: data.quantity ?? 1,
          openedAt: data.openedAt?.toDate?.() ?? new Date(),
          openedByStaffName: data.openedByStaffName ?? undefined,
          openedPrice: data.openedPrice ?? 0,
          remainingPercent: data.remainingPercent ?? 100,
          expiresAt: data.expiresAt?.toDate?.(),
          status: data.status ?? 'kept',
          location: data.location ?? undefined,
        };
      }));
      setStaff(staffSnap.docs.map((d) => ({
        id: d.id,
        name: (d.data() as any).displayName || '',
      })).filter((s) => s.name));
    } catch (e: any) {
      setError(e?.message ?? 'ボトルキープの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => { load(); }, [load]);

  const soon = Date.now() + 30 * 24 * 3600 * 1000;
  const visible = useMemo(() => {
    let list = rows;
    if (filter === 'kept') list = rows.filter((r) => r.status === 'kept');
    if (filter === 'expiring') {
      list = rows.filter((r) => r.status === 'kept' && r.expiresAt && r.expiresAt.getTime() < soon);
    }
    const q = search.trim();
    if (q) list = list.filter((r) => r.customerName.includes(q) || r.productName.includes(q));
    return list;
  }, [rows, filter, search, soon]);

  const totals = useMemo(() => {
    const kept = rows.filter((r) => r.status === 'kept');
    return {
      count: kept.length,
      value: kept.reduce((s, r) => s + r.openedPrice * (r.remainingPercent / 100), 0),
      expiring: kept.filter((r) => r.expiresAt && r.expiresAt.getTime() < soon).length,
    };
  }, [rows, soon]);

  const saveRemaining = async () => {
    const row = editDialog.row;
    if (!row) return;
    try {
      await updateDoc(doc(db, 'bottleKeeps', row.id), {
        remainingPercent: editRemaining,
        status: editRemaining <= 0 ? 'consumed' : 'kept',
        updatedAt: Timestamp.now(),
      });
      setEditDialog({ open: false, row: null });
      await load();
    } catch (e: any) {
      setError(e?.message ?? '残量の更新に失敗しました');
    }
  };

  const dispose = async (row: KeepRow) => {
    try {
      await updateDoc(doc(db, 'bottleKeeps', row.id), {
        status: 'disposed',
        updatedAt: Timestamp.now(),
      });
      await load();
    } catch (e: any) {
      setError(e?.message ?? '処分の記録に失敗しました');
    }
  };

  const addKeep = async () => {
    if (!shop || !form.customerName.trim() || !form.productName.trim()) return;
    const now = new Date();
    const expires = new Date(now);
    expires.setMonth(expires.getMonth() + form.keepMonths);
    try {
      await addDoc(collection(db, 'bottleKeeps'), {
        shopId: shop.id,
        customerName: form.customerName.trim(),
        phoneNumber: form.phoneNumber.trim() || null,
        productName: form.productName.trim(),
        quantity: form.quantity,
        openedAt: Timestamp.fromDate(now),
        openedByStaffId: form.staffId || null,
        openedByStaffName: staff.find((s) => s.id === form.staffId)?.name ?? null,
        openedPrice: form.price,
        remainingPercent: 100,
        expiresAt: Timestamp.fromDate(expires),
        status: 'kept',
        location: form.location.trim() || null,
        usageHistory: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user?.uid ?? '',
      });
      setAddDialog(false);
      setForm({ customerName: '', phoneNumber: '', productName: '', quantity: 1, price: 0, staffId: '', location: '', keepMonths: 6 });
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'キープの登録に失敗しました');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">ボトルキープ台帳</Typography>
        <Button variant="contained" onClick={() => setAddDialog(true)}>キープを登録</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">キープ中</Typography>
          <Typography variant="h5" fontWeight="bold">{totals.count} 本</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">残量ベースの評価額</Typography>
          <Typography variant="h5" fontWeight="bold">¥{Math.round(totals.value).toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: totals.expiring > 0 ? '4px solid' : undefined, borderColor: 'warning.main' }}>
          <Typography variant="caption" color="text.secondary">30日以内に期限</Typography>
          <Typography variant="h5" fontWeight="bold" color={totals.expiring > 0 ? 'warning.main' : undefined}>
            {totals.expiring} 本
          </Typography>
        </Paper>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select size="small" value={filter} sx={{ width: 180 }}
          onChange={(e) => setFilter(e.target.value as any)}
        >
          <MenuItem value="kept">キープ中</MenuItem>
          <MenuItem value="expiring">期限が近い</MenuItem>
          <MenuItem value="all">すべて</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="お客様名・銘柄で検索" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, maxWidth: 320 }}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Alert severity="info">該当するキープボトルはありません。</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>お客様</TableCell>
                <TableCell>銘柄</TableCell>
                <TableCell>入れた日</TableCell>
                <TableCell>担当</TableCell>
                <TableCell align="right">価格</TableCell>
                <TableCell sx={{ minWidth: 140 }}>残量</TableCell>
                <TableCell>期限</TableCell>
                <TableCell>棚</TableCell>
                <TableCell>状態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const expiring = r.expiresAt && r.expiresAt.getTime() < soon && r.status === 'kept';
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {r.customerName}
                      {r.phoneNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">{r.phoneNumber}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.productName}{r.quantity > 1 ? ` ×${r.quantity}` : ''}</TableCell>
                    <TableCell>{r.openedAt.toLocaleDateString('ja-JP')}</TableCell>
                    <TableCell>{r.openedByStaffName ?? '—'}</TableCell>
                    <TableCell align="right">¥{r.openedPrice.toLocaleString()}</TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, r.remainingPercent))}
                        sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                      />
                      <Typography variant="caption">{r.remainingPercent}%</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={expiring ? 'warning.main' : undefined}>
                        {r.expiresAt ? r.expiresAt.toLocaleDateString('ja-JP') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.location ?? '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={bottleKeepStatusLabels[r.status]} color={statusColor[r.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small" variant="outlined" disabled={r.status !== 'kept'}
                          onClick={() => { setEditDialog({ open: true, row: r }); setEditRemaining(r.remainingPercent); }}
                        >
                          残量
                        </Button>
                        <Button size="small" color="error" disabled={r.status !== 'kept'} onClick={() => dispose(r)}>
                          処分
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, row: null })} fullWidth maxWidth="xs">
        <DialogTitle>残量を更新</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editDialog.row?.customerName} / {editDialog.row?.productName}
          </Typography>
          <TextField
            label="残量（%）" type="number" fullWidth
            value={editRemaining}
            onChange={(e) => setEditRemaining(Math.min(100, Math.max(0, Number(e.target.value))))}
            helperText="0 にすると空きとして記録します"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, row: null })}>キャンセル</Button>
          <Button variant="contained" onClick={saveRemaining}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>キープを登録</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="お客様名（必須）" value={form.customerName}
              onChange={(e) => setForm({ ...form, customerName: e.target.value })} fullWidth />
            <TextField label="電話番号" value={form.phoneNumber}
              onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} fullWidth />
            <TextField label="銘柄（必須）" value={form.productName}
              onChange={(e) => setForm({ ...form, productName: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="本数" type="number" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Math.max(1, Number(e.target.value)) })} fullWidth />
              <TextField label="価格" type="number" value={form.price}
                onChange={(e) => setForm({ ...form, price: Math.max(0, Number(e.target.value)) })} fullWidth />
            </Stack>
            <TextField select label="入れた担当キャスト" value={form.staffId}
              onChange={(e) => setForm({ ...form, staffId: e.target.value })} fullWidth
              helperText="ボトルバックの対象になります">
              <MenuItem value="">指定しない</MenuItem>
              {staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="保管場所（棚番号）" value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} fullWidth />
              <TextField select label="キープ期限" value={form.keepMonths}
                onChange={(e) => setForm({ ...form, keepMonths: Number(e.target.value) })} fullWidth>
                <MenuItem value={3}>3ヶ月</MenuItem>
                <MenuItem value={6}>6ヶ月</MenuItem>
                <MenuItem value={12}>1年</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={addKeep}
            disabled={!form.customerName.trim() || !form.productName.trim()}>
            登録する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BottleKeeps;
